import { useTranslation } from '@jq4race/i18n';
import type { MembershipPlanResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface TogglePlanActiveDialogProps {
  plan: MembershipPlanResponse | null;
  onConfirm: () => void;
  onCancel: () => void;
}

// frequent-customer-memberships · S1b — spec Capability 1, "Plans are
// deactivated, not deleted". Same dialog handles both directions
// (deactivate/reactivate) since MembershipPlan supports both — the entity
// has no hard-delete path.
export function TogglePlanActiveDialog({ plan, onConfirm, onCancel }: TogglePlanActiveDialogProps) {
  const { t } = useTranslation('memberships');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deactivating = plan?.active ?? true;

  useEffect(() => {
    if (!plan) {
      setSubmitting(false);
      setError(null);
    }
  }, [plan]);

  function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      onConfirm();
      onCancel();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('togglePlanActiveDialog.genericError') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!plan} onOpenChange={(next) => (!next ? onCancel() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {deactivating
              ? t('togglePlanActiveDialog.deactivateTitle')
              : t('togglePlanActiveDialog.reactivateTitle')}
          </DialogTitle>
          <DialogDescription>
            {deactivating
              ? t('togglePlanActiveDialog.deactivateDescription')
              : t('togglePlanActiveDialog.reactivateDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3">
          <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
            {t('togglePlanActiveDialog.planLabel')}
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-jq-charcoal">{plan?.name}</p>
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={submitting}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant={deactivating ? 'destructive' : 'default'}
            size="sm"
            onClick={confirm}
            disabled={submitting}
          >
            {submitting
              ? t('togglePlanActiveDialog.saving')
              : deactivating
                ? t('togglePlanActiveDialog.deactivate')
                : t('togglePlanActiveDialog.reactivate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
